import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, serverTimestamp, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

import fanCornerHero from '../assets/fan-corner-hero.jpg';
import mastKalandar from '../assets/Released/mast-kalandar.jpeg';
import aa from '../assets/Released/aa.jpeg';
import mandoPlus from '../assets/Released/mando-plus.jpeg';
import kuchikuKuchiku from '../assets/Released/kuchiku-kuchiku.jpeg';

// ─── Questions loaded dynamically from JSON ──────────────────────────────────
import generalQuestionsRaw from '../assets/Questions/General.json/general.json';

// ─── Quiz configs ─────────────────────────────────────────────────────────────
const quizConfigs = [
  {
    id: 'general',
    title: 'ಸಾಮಾನ್ಯ ಜ್ಞಾನ Quiz',
    subtitle: 'Kannada Cinema General Knowledge',
    poster: null,
    icon: '🎓',
    comingSoon: false,
  },
  {
    id: 'mast-kalandar',
    title: 'ಮಸ್ತ್ ಕಲಂದರ್ Quiz',
    subtitle: 'Mast Kalandar — Audio Super Hit',
    poster: mastKalandar,
    icon: '🎵',
    comingSoon: true,
  },
  {
    id: 'aa',
    title: 'ಆ! Quiz',
    subtitle: 'Aa! — Epic Horror Story',
    poster: aa,
    icon: '👻',
    comingSoon: true,
  },
  {
    id: 'mando-plus',
    title: 'ಮ್ಯಾಂಡೋ ಪ್ಲಸ್ Quiz',
    subtitle: 'Mando Plus — Comedy Drama',
    poster: mandoPlus,
    icon: '😂',
    comingSoon: true,
  },
  {
    id: 'kuchiku-kuchiku',
    title: 'ಕುಚಿಕು ಕುಚಿಕು Quiz',
    subtitle: 'Kuchiku Kuchiku — Thriller',
    poster: kuchikuKuchiku,
    icon: '🔥',
    comingSoon: true,
  },
];

// ─── Local storage helpers (offline fallback) ─────────────────────────────
const LS_KEY = 'mms_quiz_scores';

function getLocalScores() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
}

function saveLocalScore(quizId, name, score, total) {
  const all = getLocalScores();
  if (!all[quizId]) all[quizId] = [];
  all[quizId].push({ name, score, total, date: new Date().toLocaleDateString('kn-IN') });
  all[quizId].sort((a, b) => b.score - a.score);
  localStorage.setItem(LS_KEY, JSON.stringify(all));
}

// ─── Save score to Firestore + localStorage ──────────────────────────────
async function saveScore(quizId, name, score, total) {
  // Always save locally as fallback
  saveLocalScore(quizId, name, score, total);
  // Save to Firestore
  try {
    await addDoc(collection(db, 'quizScores'), {
      quizId,
      name,
      score,
      total,
      percentage: Math.round((score / total) * 100),
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Firestore save failed, local score saved:', err);
  }
}

// ─── Pick N random questions fresh on every call ─────────────────────────────
function pickRandom(arr, n) {
  return [...arr].sort(() => Math.random() - 0.5).slice(0, n);
}

// ─── Rankings Modal (reads from Firestore) ────────────────────────────────
function RankingsModal({ quizId, quizTitle, onClose }) {
  const [scores, setScores] = useState([]);
  const [loadingRanks, setLoadingRanks] = useState(true);

  useEffect(() => {
    const fetchRanks = async () => {
      try {
        const q = query(
          collection(db, 'quizScores'),
          where('quizId', '==', quizId),
          orderBy('score', 'desc')
        );
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({
          name: d.data().name,
          score: d.data().score,
          total: d.data().total,
          date: d.data().createdAt?.toDate?.().toLocaleDateString('kn-IN') || '—',
        }));
        setScores(data);
      } catch (err) {
        // Fall back to localStorage
        const local = getLocalScores()[quizId] || [];
        setScores(local);
      } finally {
        setLoadingRanks(false);
      }
    };
    fetchRanks();
  }, [quizId]);

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ background: 'rgba(5,5,5,0.92)', backdropFilter: 'blur(14px)' }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-lg rounded-sm overflow-hidden"
        initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(160deg,#0f0f0f,#1a1410)',
          border: '1px solid rgba(212,175,55,0.3)',
          boxShadow: '0 0 80px rgba(212,175,55,0.12)',
          maxHeight: '85vh', overflowY: 'auto',
        }}
      >
        <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-brand-gold/60 pointer-events-none z-10" />
        <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-brand-gold/60 pointer-events-none z-10" />

        <div className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <span className="section-label block mb-2">🏆 Rankings</span>
            <h3 className="font-cinzel font-bold text-white text-xl sm:text-2xl">{quizTitle}</h3>
            <div className="gold-divider mt-3" />
          </div>

          {loadingRanks ? (
            <div className="text-center py-10">
              <div className="w-8 h-8 border-2 border-brand-gold/30 border-t-brand-gold rounded-full animate-spin mx-auto mb-3" />
              <p className="font-inter text-white/30 text-xs">Loading rankings...</p>
            </div>
          ) : scores.length === 0 ? (
            <div className="text-center py-10">
              <div className="text-5xl mb-4">🎬</div>
              <p className="font-cinzel text-white/40 text-sm">ಇನ್ನೂ ಯಾರೂ ಆಟ ಆಡಿಲ್ಲ!</p>
              <p className="font-inter text-white/25 text-xs mt-1">No scores yet. Be the first!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scores.map((entry, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="flex items-center gap-4 p-3 sm:p-4 rounded-sm"
                  style={{
                    background: idx === 0 ? 'rgba(212,175,55,0.12)' : idx === 1 ? 'rgba(192,192,192,0.08)' : idx === 2 ? 'rgba(205,127,50,0.08)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${idx === 0 ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-cinzel font-bold text-sm"
                    style={{
                      background: idx === 0 ? 'linear-gradient(135deg,#D4AF37,#f37021)' : idx === 1 ? 'rgba(192,192,192,0.3)' : idx === 2 ? 'rgba(205,127,50,0.3)' : 'rgba(255,255,255,0.1)',
                      color: idx < 3 ? '#0f0f0f' : '#fff',
                    }}>
                    {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-cinzel text-white text-sm font-semibold truncate">{entry.name}</p>
                    <p className="font-inter text-white/40 text-[10px]">{entry.date}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-cinzel font-bold text-brand-gold text-base">
                      {entry.score}<span className="text-white/30 text-xs">/{entry.total}</span>
                    </p>
                    <p className="font-inter text-white/40 text-[10px]">{Math.round((entry.score / entry.total) * 100)}%</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <button
            onClick={onClose}
            className="mt-6 w-full font-cinzel text-xs tracking-widest uppercase py-3 rounded-sm transition-all duration-300 text-white/60 hover:text-brand-gold"
            style={{ border: '1px solid rgba(212,175,55,0.25)' }}
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Quiz Modal ───────────────────────────────────────────────────────────────
function QuizModal({ quiz, onClose }) {
  const [phase, setPhase] = useState('name'); // name | playing | result
  const [playerName, setPlayerName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [shake, setShake] = useState(false);

  // Fetch fresh random 10 questions from JSON on every quiz start
  const startQuiz = () => {
    if (!playerName.trim()) {
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    const fresh = pickRandom(generalQuestionsRaw, 10);
    setQuestions(fresh);
    setScore(0);
    setCurrent(0);
    setSelected(null);
    setPhase('playing');
  };

  const handleAnswer = useCallback((opt) => {
    if (selected !== null) return;
    setSelected(opt);
    if (opt === questions[current].correctOption) {
      setScore(s => s + 1);
    }
  }, [selected, questions, current]);

  const next = async () => {
    if (current + 1 >= questions.length) {
      await saveScore(quiz.id, playerName.trim(), score, questions.length);
      setPhase('result');
    } else {
      setCurrent(c => c + 1);
      setSelected(null);
    }
  };

  const getOptionText = (q, opt) => {
    if (opt === 'A') return q.optionA;
    if (opt === 'B') return q.optionB;
    if (opt === 'C') return q.optionC;
    return '';
  };

  const pct = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
  const optionKeys = ['A', 'B', 'C'];

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ background: 'rgba(5,5,5,0.94)', backdropFilter: 'blur(16px)' }}
    >
      <motion.div
        className="relative w-full max-w-2xl rounded-sm overflow-hidden"
        initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{
          background: 'linear-gradient(160deg,#0f0f0f 0%,#1a1410 60%,#0f0f0f 100%)',
          border: '1px solid rgba(212,175,55,0.25)',
          boxShadow: '0 0 100px rgba(243,112,33,0.12)',
          maxHeight: '92vh', overflowY: 'auto',
        }}
      >
        {/* Corners */}
        <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-brand-gold/60 pointer-events-none z-10" />
        <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-brand-gold/60 pointer-events-none z-10" />

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-sm text-white/50 hover:text-brand-gold transition-all"
          style={{ border: '1px solid rgba(212,175,55,0.2)', background: 'rgba(15,15,15,0.8)' }}
        >
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div className="p-5 sm:p-8">
          <AnimatePresence mode="wait">

            {/* ── Name Phase ── */}
            {phase === 'name' && (
              <motion.div
                key="name"
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                className="text-center"
              >
                <div className="text-5xl mb-4">🎓</div>
                <span className="section-label block mb-3">Fan Quiz</span>
                <h3 className="font-cinzel font-bold text-white text-xl sm:text-2xl mb-1">{quiz.title}</h3>
                <p className="font-inter text-white/40 text-xs mb-1">{quiz.subtitle}</p>
                <p className="font-inter text-white/30 text-[10px] mb-6">
                  10 ಯಾದೃಚ್ಛಿಕ ಪ್ರಶ್ನೆಗಳು &bull; {generalQuestionsRaw.length} ಪ್ರಶ್ನೆಗಳ ಬ್ಯಾಂಕ್ &bull; ಪ್ರತಿ ಬಾರಿ ಬೇರೆ
                </p>
                <div className="gold-divider mb-8" />

                <div className="text-left mb-6">
                  <label className="font-cinzel text-[10px] tracking-widest uppercase text-brand-gold/70 block mb-2">
                    ನಿಮ್ಮ ಹೆಸರು ನಮೂದಿಸಿ
                  </label>
                  <motion.input
                    animate={shake ? { x: [-8, 8, -6, 6, 0] } : {}}
                    transition={{ duration: 0.4 }}
                    type="text"
                    placeholder="Enter your name..."
                    value={playerName}
                    onChange={e => setPlayerName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && startQuiz()}
                    className="w-full px-4 py-3 rounded-sm font-inter text-white text-sm outline-none"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: `1px solid ${shake ? '#f37021' : 'rgba(212,175,55,0.25)'}`,
                      transition: 'border-color 0.3s',
                    }}
                    autoFocus
                  />
                </div>
                <button onClick={startQuiz} className="btn-royal w-full py-4 rounded-sm text-sm tracking-widest">
                  ಪ್ರಶ್ನೋತ್ತರ ಪ್ರಾರಂಭಿಸಿ 🎬
                </button>
              </motion.div>
            )}

            {/* ── Playing Phase ── */}
            {phase === 'playing' && questions.length > 0 && (
              <motion.div
                key={`q-${current}`}
                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.28 }}
              >
                {/* Progress */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-cinzel text-[10px] tracking-widest uppercase text-brand-orange">
                      ಪ್ರಶ್ನೆ {current + 1} / {questions.length}
                    </span>
                    <span className="font-cinzel text-[10px] tracking-widest text-brand-gold">
                      {score} ಸರಿ
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: 'linear-gradient(90deg,#f37021,#D4AF37)' }}
                      initial={{ width: `${(current / questions.length) * 100}%` }}
                      animate={{ width: `${((current + 1) / questions.length) * 100}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>

                {/* Question */}
                <div
                  className="mb-6 p-4 sm:p-5 rounded-sm"
                  style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.12)' }}
                >
                  <p className="font-cinzel text-white text-sm sm:text-base leading-relaxed">
                    {questions[current].question}
                  </p>
                </div>

                {/* Options */}
                <div className="space-y-3 mb-6">
                  {optionKeys.map(opt => {
                    const text = getOptionText(questions[current], opt);
                    const isCorrect = questions[current].correctOption === opt;
                    const isSelected = selected === opt;

                    let bg = 'rgba(255,255,255,0.04)';
                    let border = 'rgba(255,255,255,0.08)';
                    let color = 'rgba(255,255,255,0.8)';

                    if (selected !== null) {
                      if (isCorrect)       { bg = 'rgba(34,197,94,0.15)';  border = 'rgba(34,197,94,0.5)';  color = '#4ade80'; }
                      else if (isSelected) { bg = 'rgba(239,68,68,0.15)';  border = 'rgba(239,68,68,0.5)';  color = '#f87171'; }
                    } else if (isSelected) { bg = 'rgba(243,112,33,0.15)'; border = 'rgba(243,112,33,0.5)'; color = '#f37021'; }

                    return (
                      <motion.button
                        key={opt}
                        whileHover={!selected ? { scale: 1.01 } : {}}
                        whileTap={!selected ? { scale: 0.99 } : {}}
                        onClick={() => handleAnswer(opt)}
                        className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-sm transition-all duration-250"
                        style={{ background: bg, border: `1px solid ${border}`, cursor: selected ? 'default' : 'pointer' }}
                      >
                        <span
                          className="w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 font-cinzel font-bold text-xs"
                          style={{ border: `1px solid ${border}`, color }}
                        >
                          {opt}
                        </span>
                        <span className="font-inter text-sm leading-snug" style={{ color }}>{text}</span>
                        {selected !== null && isCorrect  && <span className="ml-auto text-green-400 text-lg">✓</span>}
                        {selected !== null && isSelected && !isCorrect && <span className="ml-auto text-red-400 text-lg">✗</span>}
                      </motion.button>
                    );
                  })}
                </div>

                {selected !== null && (
                  <motion.button
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    onClick={next}
                    className="btn-royal w-full py-3 rounded-sm text-xs tracking-widest"
                  >
                    {current + 1 >= questions.length ? 'ಫಲಿತಾಂಶ ನೋಡಿ 🏁' : 'ಮುಂದಿನ ಪ್ರಶ್ನೆ →'}
                  </motion.button>
                )}
              </motion.div>
            )}

            {/* ── Result Phase ── */}
            {phase === 'result' && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="text-6xl mb-4">
                  {pct >= 80 ? '🏆' : pct >= 60 ? '🎬' : pct >= 40 ? '🎭' : '🎞️'}
                </div>
                <span className="section-label block mb-2">ಫಲಿತಾಂಶ</span>
                <h3 className="font-cinzel font-bold text-white text-2xl sm:text-3xl mb-1">{playerName}</h3>
                <div className="gold-divider my-4" />

                <div
                  className="mb-6 p-5 rounded-sm"
                  style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}
                >
                  <p className="font-inter text-white/50 text-xs mb-2">ನಿಮ್ಮ ಸ್ಕೋರ್</p>
                  <p
                    className="font-cinzel font-black text-5xl"
                    style={{ background: 'linear-gradient(135deg,#D4AF37,#f37021)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                  >
                    {score}<span className="text-2xl" style={{ WebkitTextFillColor: 'rgba(255,255,255,0.2)' }}>/{questions.length}</span>
                  </p>
                  <p className="font-cinzel text-brand-gold text-lg mt-1">{pct}%</p>
                  <p className="font-inter text-white/40 text-xs mt-3">
                    {pct >= 80 ? '🌟 ಅದ್ಭುತ! ನೀವು ನಿಜವಾದ ಸಿನಿಮಾ ತಜ್ಞ!' :
                     pct >= 60 ? '👍 ಚೆನ್ನಾಗಿ ಆಡಿದ್ದೀರಿ!' :
                     pct >= 40 ? '📽️ ಇನ್ನೂ ಕಲಿಯಿರಿ!' : '🎬 ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ!'}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => { setPhase('name'); setPlayerName(''); setQuestions([]); setScore(0); }}
                    className="flex-1 btn-royal py-3 rounded-sm text-xs tracking-widest"
                  >
                    ಮತ್ತೆ ಆಡಿ 🔄
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 font-cinzel text-xs tracking-widest uppercase py-3 rounded-sm transition-all text-white/60 hover:text-brand-gold"
                    style={{ border: '1px solid rgba(212,175,55,0.25)' }}
                  >
                    ಮುಗಿಸಿ
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Quiz Card ────────────────────────────────────────────────────────────────
function QuizCard({ quiz, index }) {
  const [showQuiz, setShowQuiz] = useState(false);
  const [showRanks, setShowRanks] = useState(false);
  const scores = getLocalScores()[quiz.id] || [];
  const topScore = scores[0];

  if (quiz.comingSoon) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className="relative rounded-sm overflow-hidden"
        style={{
          background: 'linear-gradient(160deg,#0d0d0d,#151510)',
          border: '1px solid rgba(212,175,55,0.08)',
        }}
      >
        {/* Dim poster bg */}
        {quiz.poster && (
          <div className="absolute inset-0">
            <img src={quiz.poster} alt={quiz.title} className="w-full h-full object-cover opacity-8" style={{ filter: 'grayscale(80%) brightness(0.3)' }} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/90 to-[#0d0d0d]/70" />
          </div>
        )}

        <div className="relative z-10 p-5 sm:p-6">
          <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-4"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <span className="text-2xl opacity-40">{quiz.icon}</span>
          </div>

          <h3 className="font-cinzel font-bold text-white/30 text-base sm:text-lg mb-1">{quiz.title}</h3>
          <p className="font-inter text-white/20 text-xs mb-5">{quiz.subtitle}</p>

          {/* Coming Soon Badge */}
          <div className="flex items-center justify-center py-5">
            <div className="text-center px-6 py-4 rounded-sm" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)' }}>
              <div className="text-2xl mb-2">🎬</div>
              <p className="font-cinzel text-brand-gold/60 text-xs tracking-widest uppercase">Coming Soon</p>
              <p className="font-inter text-white/25 text-[10px] mt-1">Quiz ಶೀಘ್ರದಲ್ಲೇ ಬರಲಿದೆ</p>
            </div>
          </div>

          {/* Disabled buttons */}
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div
              className="py-3 rounded-sm text-center font-cinzel text-[10px] tracking-widest uppercase text-white/15"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              ▶ ಪ್ರಶ್ನೋತ್ತರ
            </div>
            <div
              className="py-3 rounded-sm text-center font-cinzel text-[10px] tracking-widest uppercase text-white/15"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              🏆 ರ್ಯಾಂಕ್ಸ್
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Active quiz card (General) ──
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className="relative rounded-sm overflow-hidden group"
        style={{
          background: 'linear-gradient(160deg,#0f0f0f,#1a1410)',
          border: '1px solid rgba(212,175,55,0.18)',
          transition: 'box-shadow 0.3s',
        }}
        whileHover={{ boxShadow: '0 20px 60px rgba(243,112,33,0.2)' }}
      >
        {/* Diagonal pattern bg */}
        <div className="absolute inset-0 opacity-10" style={{
          background: 'repeating-linear-gradient(45deg,transparent,transparent 20px,rgba(212,175,55,0.15) 20px,rgba(212,175,55,0.15) 21px)'
        }} />

        <div className="relative z-10 p-5 sm:p-6">
          <div className="w-12 h-12 rounded-sm flex items-center justify-center mb-4"
            style={{ background: 'linear-gradient(135deg,#D4AF37,#f37021)' }}>
            <span className="text-2xl">🎓</span>
          </div>

          <h3 className="font-cinzel font-bold text-white text-base sm:text-lg mb-1">{quiz.title}</h3>
          <p className="font-inter text-white/40 text-xs mb-4">{quiz.subtitle}</p>

          {/* Stats */}
          <div className="flex items-center gap-4 mb-5 p-3 rounded-sm"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="text-center flex-1">
              <p className="font-cinzel font-bold text-brand-gold text-lg">10</p>
              <p className="font-inter text-white/30 text-[9px] uppercase tracking-widest">ಪ್ರಶ್ನೆಗಳು</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center flex-1">
              <p className="font-cinzel font-bold text-brand-orange text-lg">{generalQuestionsRaw.length}</p>
              <p className="font-inter text-white/30 text-[9px] uppercase tracking-widest">ಬ್ಯಾಂಕ್</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center flex-1">
              <p className="font-cinzel font-bold text-white text-lg">{scores.length}</p>
              <p className="font-inter text-white/30 text-[9px] uppercase tracking-widest">ಆಟಗಾರರು</p>
            </div>
          </div>

          {/* Top player */}
          {topScore ? (
            <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-sm"
              style={{ background: 'rgba(212,175,55,0.07)', border: '1px solid rgba(212,175,55,0.15)' }}>
              <span>🥇</span>
              <span className="font-cinzel text-brand-gold text-xs truncate">{topScore.name}</span>
              <span className="ml-auto font-cinzel text-white/50 text-xs">{topScore.score}/{topScore.total}</span>
            </div>
          ) : (
            <div className="mb-5 px-3 py-2 rounded-sm text-center"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="font-inter text-white/25 text-[10px]">ಮೊದಲಿಗರಾಗಿ! Be the first to play</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setShowQuiz(true)}
              className="btn-royal py-3 rounded-sm text-[10px] tracking-widest flex items-center justify-center gap-1.5"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              ಪ್ರಶ್ನೋತ್ತರ
            </button>
            <button
              onClick={() => setShowRanks(true)}
              className="font-cinzel text-[10px] tracking-widest uppercase py-3 rounded-sm transition-all duration-300 text-brand-gold hover:text-white flex items-center justify-center gap-1.5"
              style={{ border: '1px solid rgba(212,175,55,0.3)', background: 'rgba(212,175,55,0.05)' }}
            >
              <span>🏆</span> ರ್ಯಾಂಕ್ಸ್
            </button>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {showQuiz  && <QuizModal quiz={quiz} onClose={() => setShowQuiz(false)} />}
        {showRanks && <RankingsModal quizId={quiz.id} quizTitle={quiz.title} onClose={() => setShowRanks(false)} />}
      </AnimatePresence>
    </>
  );
}

// ─── Main Fan Corner Page ─────────────────────────────────────────────────────
export default function FanCorner() {
  return (
    <div className="relative bg-brand-black min-h-screen">

      {/* Hero */}
      <div className="relative h-[55vh] sm:h-[65vh] overflow-hidden">
        <img
          src={fanCornerHero}
          alt="Fan Corner"
          className="w-full h-full object-cover"
          style={{ filter: 'brightness(0.55)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-brand-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black/60 to-transparent" />

        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-4"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
        >
          <motion.span className="section-label block mb-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
            Magic Moon Studios
          </motion.span>
          <motion.h1
            className="font-cinzel font-black text-4xl sm:text-5xl md:text-7xl text-white mb-4 leading-tight"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          >
            ಫ್ಯಾನ್ <span className="text-gold-gradient">ಕಾರ್ನರ್</span>
          </motion.h1>
          <div className="gold-divider mb-5" />
          <motion.p
            className="font-inter text-white/60 max-w-lg text-sm leading-relaxed"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          >
            ನಿಮ್ಮ ಸಿನಿಮಾ ಜ್ಞಾನ ಪರೀಕ್ಷಿಸಿ &bull; Test your Kannada cinema knowledge!
          </motion.p>
        </motion.div>
      </div>

      {/* Quiz Section */}
      <section className="relative py-16 sm:py-20 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" />
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-orange/5 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-brand-gold/5 blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          >
            <span className="section-label block mb-4">ಕ್ವಿಜ್‌ಗಳು</span>
            <h2 className="font-cinzel font-black text-3xl sm:text-4xl text-white mb-4">
              ಯಾವ <span className="text-gold-gradient">Quiz</span> ಆಡಲು ಬಯಸುವಿರಿ?
            </h2>
            <div className="gold-divider mb-5" />
            <p className="font-inter text-white/40 text-sm max-w-lg mx-auto">
              ಪ್ರತಿ ಬಾರಿ {generalQuestionsRaw.length} ಪ್ರಶ್ನೆಗಳ ಬ್ಯಾಂಕ್‌ನಿಂದ 10 ಯಾದೃಚ್ಛಿಕ ಪ್ರಶ್ನೆಗಳು ಬರುತ್ತವೆ
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {quizConfigs.map((quiz, i) => (
              <QuizCard key={quiz.id} quiz={quiz} index={i} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
